/**
 * Cross-tab coordination for silent token renewals.
 *
 * Why this exists: the `signinSilent` wrapper in `userManagerSingleton.ts`
 * coalesces concurrent renewals WITHIN one tab, but nothing coordinated
 * across tabs. With Keycloak refresh-token rotation ("Revoke Refresh Token"),
 * two tabs renewing with the same stored refresh token race: the loser's
 * POST /token gets 400 `invalid_grant` ("Session not active"), which the app
 * treats as a definitive session-end and bounces the user to login — while
 * the winning tab holds a perfectly good session (CUSTOMER-UI-57, "bounced
 * out while actively using the app").
 *
 * Two protections, both keyed on the shared localStorage user store:
 *  1. A Web Lock (`navigator.locks`) serializes renewals per origin. After
 *     acquiring the lock, we re-read the store — if another tab already
 *     renewed while we waited, we adopt its user and skip the network call
 *     entirely, so only one tab per origin ever POSTs per renewal window.
 *  2. If the renewal still fails with a session-end shape, ONE storage
 *     recheck runs before the failure propagates: a race lost just before
 *     our POST (or on a browser without Web Locks) leaves the winner's
 *     fresher user in storage, which we adopt instead of bouncing. A
 *     genuinely dead session leaves nothing fresh to adopt, so the original
 *     error is rethrown unchanged and the existing redirect path is intact.
 *
 * NOT a blind retry of `invalid_grant`: replaying a rotated refresh token
 * trips Keycloak's reuse detection, which can revoke the whole session.
 */

export const OIDC_RENEW_LOCK_NAME = "oidc-silent-renew";

export type LockRequest = <T>(fn: () => Promise<T>) => Promise<T>;

/**
 * Run `fn` while holding the origin-wide renewal Web Lock. Web Locks are
 * supported by all evergreen browsers; where unavailable the function runs
 * unlocked (previous behavior — the storage-recheck layer still applies).
 */
export const requestCrossTabRenewLock: LockRequest = <T>(
  fn: () => Promise<T>
): Promise<T> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks?.request) return fn();
  return locks.request(OIDC_RENEW_LOCK_NAME, fn) as Promise<T>;
};

export interface CoordinatedSilentRenewOptions<TUser> {
  /**
   * Read the shared user store and return the stored user ONLY if it proves
   * another tab renewed since this renewal became necessary (non-expired AND
   * different from the token this renewal started with), else null.
   */
  getFreshStoredUser: () => Promise<TUser | null>;
  /**
   * Make an adopted stored user the active session (raise USER_LOADED so
   * react-oidc-context and the token-expiry timers pick it up).
   */
  adoptStoredUser: (user: TUser) => Promise<void>;
  /** The actual renewal (the retry-wrapped `signinSilent`). */
  renew: () => Promise<TUser | null>;
  /** Definitive session-end shape (`invalid_grant` / "Session not active"…). */
  isSessionEndError: (error: unknown) => boolean;
  /** Injectable for tests; production uses the Web Lock. */
  requestLock?: LockRequest;
}

export async function coordinatedSilentRenew<TUser>({
  getFreshStoredUser,
  adoptStoredUser,
  renew,
  isSessionEndError,
  requestLock = requestCrossTabRenewLock,
}: CoordinatedSilentRenewOptions<TUser>): Promise<TUser | null> {
  return requestLock(async () => {
    // Another tab may have renewed while we waited on the lock.
    const alreadyRenewed = await getFreshStoredUser();
    if (alreadyRenewed) {
      await adoptStoredUser(alreadyRenewed);
      return alreadyRenewed;
    }
    try {
      return await renew();
    } catch (error) {
      // A session-end failure can be a rotation race we lost (winner's user
      // is in storage) rather than a genuinely dead session. One recheck.
      if (isSessionEndError(error)) {
        const recovered = await getFreshStoredUser();
        if (recovered) {
          await adoptStoredUser(recovered);
          return recovered;
        }
      }
      throw error;
    }
  });
}
